import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useResearchContext, authHeader } from 'layouts/user/Research/ResearchContext.provider';
import UserMessageView from '../UserMessageView/UserMessageView.component';
import { Image } from 'components/Image/Image';
import enkiLogo from 'assets/img/icons/enki-logo-light.png';
import { UserData } from '$models/user/UserData.model';
import { useStickyIntro } from 'hooks/useStickyIntro';
import ChatContentView from '../ChatContentView';
import './ChatStreamView.scss';

export type ChatStreamViewProps = {
  userProfile: UserData | null;
};

const ChatStreamView: React.FC<ChatStreamViewProps> = ({ userProfile }) => {
  const { state, actions } = useResearchContext();
  const { chat: chats, streaming, images, presentationStatusMessage } = state;
  const [stickyIndex, setStickyIndex] = useState<number | null>(null);
  const [hasScrollableArea, setHasScrollableArea] = useState<boolean>(true);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { startStickyIntro, hasStarted: stickyIntroStarted } = useStickyIntro();
  const historyContainer = document.querySelector('.research__form__history-container') as HTMLElement;
  const stickyOffset = 85;
  // Depend on chats.length rather than the `chats` array reference itself.
  // During streaming, every chunk replaces the last (assistant) message with a
  // new object/array reference even though no user message index changes -
  // recomputing this on every chunk would give it a new identity each time
  // too, which (see handleScroll/the scroll-listener effect below) cascades
  // into tearing down and rebuilding the scroll listener on every chunk and
  // can drop in-flight scroll updates, freezing the sticky header on
  // whichever question was current when streaming started.
  const userQuestionIndices = useMemo(() =>
    chats
      .map((chat, index) => (chat && chat.role === 'user' && chat.content?.trim()) ? index : -1)
      .filter(index => index !== -1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chats.length]
  );

  // Find the last user message index to correctly determine isLastQuestion
  // This ensures buttons remain visible even when new assistant messages are added (e.g., from Presentation dropdown)
  const lastUserQuestionIndex = useMemo(() =>
    userQuestionIndices.length > 0
      ? userQuestionIndices[userQuestionIndices.length - 1]
      : -1,
    [userQuestionIndices]
  );

  const prevHasScrollableAreaRef = useRef<boolean>(true);

  const checkScrollableArea = useCallback(() => {
    if (!historyContainer) return;
    const containerHeight = historyContainer.clientHeight;
    const scrollHeight = historyContainer.scrollHeight;
    const scrollableHeight = scrollHeight - containerHeight;

    const shouldEnableScrollableArea = scrollableHeight >= stickyOffset;

    // Only update state if value actually changed
    if (prevHasScrollableAreaRef.current !== shouldEnableScrollableArea) {
      prevHasScrollableAreaRef.current = shouldEnableScrollableArea;
      setHasScrollableArea(shouldEnableScrollableArea);
    }
  }, [historyContainer]);

  useEffect(() => {
    checkScrollableArea();
  }, [streaming, checkScrollableArea]);

  const handleContentClick = useCallback(async (event: React.MouseEvent) => {
    const target = event.target as HTMLElement;

    // Handle Image Click
    if (target.tagName === 'IMG' &&
      (target.classList.contains('presentation-image') || target.classList.contains('explore-chart-image'))) {
      const imgSrc = (target as HTMLImageElement).src;
      setFullscreenImage(imgSrc);
      return;
    }

    // Handle Download Presentation Click
    const downloadBtn = target.closest('.download-presentation-btn');
    if (downloadBtn) {
      const btn = downloadBtn as HTMLButtonElement;
      const originalText = btn.innerHTML;

      // Add loading state
      btn.innerHTML = '<div class="typing" style="margin: 0; display: inline-block;"><span></span><span></span><span></span></div> Generating...';
      btn.disabled = true;
      btn.style.cursor = 'wait';

      try {
        const HOST = process.env.REACT_APP_SERVER;
        // Collect current chat to send to backend
        // We probably only need the last User/Assistant pair related to the presentation? 
        // But sending full chat is safer for context.
        const response = await fetch(`${HOST}/api/analysis/download-presentation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({ chat: chats })
        });

        if (!response.ok) throw new Error('Download failed');

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `enki-presentation-${new Date().getFullYear()}.pptx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Presentation download error:", err);
        // Toast message would be better here via dispatch(pushToastMessage) if available in scope
        alert("Failed to generate/download presentation. Please try again.");
      } finally {
        btn.innerHTML = originalText;
        btn.disabled = false;
        btn.style.cursor = 'pointer';
      }
    }
  }, [chats]);

  const closeFullscreenImage = useCallback(() => {
    setFullscreenImage(null);
  }, []);

  const handleScroll = useCallback(() => {
    checkScrollableArea();

    if (!historyContainer || userQuestionIndices.length === 0) return;

    const containerRect = historyContainer.getBoundingClientRect();
    let newStickyIndex: number | null = null;

    // Loop backwards to find the LATEST question that has scrolled past the top
    // this ensures we show the header for the section the user is currently reading
    for (let i = userQuestionIndices.length - 1; i >= 0; i--) {
      const userQuestionIndex = userQuestionIndices[i];
      const userMessageElement = messageRefs.current[userQuestionIndex];

      if (!userMessageElement) continue;

      const userMessageRect = userMessageElement.getBoundingClientRect();

      let sectionEndIndex = chats.length - 1;
      if (i + 1 < userQuestionIndices.length) {
        sectionEndIndex = userQuestionIndices[i + 1] - 1;
      }

      const sectionEndElement = messageRefs.current[sectionEndIndex];
      let sectionEndTop = Number.MAX_SAFE_INTEGER;
      if (sectionEndElement) {
        const sectionEndRect = sectionEndElement.getBoundingClientRect();
        sectionEndTop = sectionEndRect.bottom;
      }

      const userMessageTop = userMessageRect.top;
      const userMessageBottom = userMessageRect.bottom;
      const containerTop = containerRect.top + stickyOffset;

      const questionHasScrolledPast = userMessageBottom <= containerTop;
      const sectionStillVisible = sectionEndTop > containerTop;
      const questionBackInView = userMessageTop >= containerTop - 10;

      let shouldBeSticky = questionHasScrolledPast && sectionStillVisible && !questionBackInView;

      // If the next question has reached the sticky area, this section is over
      if (shouldBeSticky && i + 1 < userQuestionIndices.length) {
        const nextQuestionEl = messageRefs.current[userQuestionIndices[i + 1]];
        if (nextQuestionEl) {
          const nextQuestionTop = nextQuestionEl.getBoundingClientRect().top;
          if (nextQuestionTop <= containerTop) {
            shouldBeSticky = false;
          }
        }
      }

      if (shouldBeSticky) {
        if (!stickyIntroStarted) {
          const { stickyIntroSteps, STICKY_INTRO_COOKIE_KEY } = require('../../reportIntro.steps');

          startStickyIntro({
            scrollContainer: historyContainer,
            introSteps: stickyIntroSteps,
            cookieKey: STICKY_INTRO_COOKIE_KEY,
            cookieExpireDays: 365,
            delay: 1000
          });
        }

        newStickyIndex = userQuestionIndex;
        break; // Found the latest sticky index, stop looking
      }
    }

    setStickyIndex(newStickyIndex);
  }, [historyContainer, userQuestionIndices, chats.length, stickyOffset, streaming, checkScrollableArea]);

  // Use requestAnimationFrame for throttled scroll handling
  const rafRef = useRef<number | null>(null);

  // Keep the listener itself independent of handleScroll's identity. handleScroll
  // is recreated whenever userQuestionIndices/chats.length/streaming change, and
  // re-subscribing on every one of those changes would tear down (and cancel any
  // in-flight requestAnimationFrame for) the listener each time - dropping scroll
  // updates that happen to land in that window. Routing calls through a ref lets
  // the effect below only (re)subscribe when it actually needs to.
  const handleScrollRef = useRef(handleScroll);
  useEffect(() => {
    handleScrollRef.current = handleScroll;
  }, [handleScroll]);

  useEffect(() => {
    if (!historyContainer) return;

    const throttledHandleScroll = () => {
      // Cancel any pending animation frame
      if (rafRef.current !== null) {
        return; // Already scheduled, skip this call
      }

      rafRef.current = requestAnimationFrame(() => {
        handleScrollRef.current();
        rafRef.current = null;
      });
    };

    historyContainer.addEventListener('scroll', throttledHandleScroll);
    window.addEventListener('resize', throttledHandleScroll);

    checkScrollableArea();
    const t = setTimeout(() => handleScrollRef.current(), 100);

    return () => {
      historyContainer.removeEventListener('scroll', throttledHandleScroll);
      window.removeEventListener('resize', throttledHandleScroll);
      clearTimeout(t);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [chats.length, userQuestionIndices.length, checkScrollableArea, streaming, historyContainer]);

  return (
    <>
      <div className="chat-stream-view" data-testid="chat-stream-view" ref={chatContainerRef}>
        {chats.map((chatMessage, index) => {
          if (!chatMessage) return null;
          return (
            <div
              key={index}
              className="chat-stream-view-item"
              data-testid="chat-stream-view-item"
              ref={el => { messageRefs.current[index] = el; }}
            >
              {chatMessage.role === 'user' ? (
                <UserMessageView
                  message={chatMessage}
                  userProfile={userProfile}
                  index={index}
                  isLastQuestion={index === lastUserQuestionIndex}
                  isSticky={stickyIndex === index && hasScrollableArea}
                  hasScrollableArea={hasScrollableArea}
                />
              ) : (
                <div className={"research__form__" + chatMessage.role}>
                  <div className="research__form__image"><Image src={enkiLogo} /></div>
                  <div className="research__form__speaker">ENKI</div>
                  {presentationStatusMessage && index === chats.length - 1 && streaming && !chatMessage.content.trim() ? (
                    <div className="research__form__message">
                      <span className="typing-message">{presentationStatusMessage}</span>
                      <div className="typing">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <ChatContentView
                        content={chatMessage.content}
                        className="research__form__message"
                        onClick={handleContentClick}
                      />
                      {index in images && (
                        <div
                          className="research__form__message"
                          dangerouslySetInnerHTML={{ __html: images[index] }}
                          onClick={handleContentClick}
                        />
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {fullscreenImage && (
        <div className="image-fullscreen-modal" onClick={closeFullscreenImage}>
          <div className="image-fullscreen-modal__close">
            <i className="material-icons">close</i>
          </div>
          <img
            src={fullscreenImage}
            alt="Fullscreen presentation"
            className="image-fullscreen-modal__image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default ChatStreamView;